import type { Layout } from '../../data/types';
import type { Rng } from '../../core/rng';
import type { Vec2, Vec3 } from '../../geo/types';

export interface BirdFlight {
  readonly centre: Vec2;
  readonly radius: Vec2;
  readonly altitude: number;
  readonly phase: number;
  readonly speed: number;
  readonly scale: number;
}

/** Waterbird groups over the harbour, canal corridors and civic squares; scene dressing. */
export function planBirdFlights(
  rng: Rng,
  layout: Pick<Layout, 'landmarks'>
): readonly BirdFlight[] {
  const centres: Vec2[] = [
    [435, 610],
    [265, 255],
    [665, 360],
    [5, -8],
    [170, 345],
    [340, 470],
    [420, 160],
    [-100, 210],
  ];
  const candidates = layout.landmarks.map((landmark) => landmark.pos);
  while (centres.length < 24 && candidates.length) {
    let selected = 0,
      widest = -1;
    candidates.forEach((point, i) => {
      const spacing = Math.min(...centres.map((c) => Math.hypot(c[0] - point[0], c[1] - point[1])));
      if (spacing > widest) {
        widest = spacing;
        selected = i;
      }
    });
    centres.push(candidates.splice(selected, 1)[0]!);
  }
  return centres.flatMap((centre, flock) =>
    Array.from({ length: 12 }, (_, i) => ({
      centre,
      radius: [rng.range(18, 45), rng.range(24, 58)] as Vec2,
      altitude:
        flock >= 8
          ? rng.range(75, 90)
          : flock === 1 || flock >= 4
            ? rng.range(32, 40)
            : rng.range(42, 58),
      phase: (i * Math.PI) / 6 + rng.range(-0.25, 0.25),
      speed: rng.range(0.08, 0.14),
      scale: rng.range(0.85, 1.15),
    }))
  );
}

export function birdPoseAt(flight: BirdFlight, time: number) {
  const angle = flight.phase + time * flight.speed;
  const x = flight.centre[0] + Math.cos(angle) * flight.radius[0];
  const y = flight.centre[1] + Math.sin(angle) * flight.radius[1];
  const vx = -Math.sin(angle) * flight.radius[0];
  const vy = Math.cos(angle) * flight.radius[1];
  // Long glides alternate smoothly with brief wingbeats, without resetting the orbit.
  const glide = Math.max(0, Math.sin(time * 0.37 + flight.phase) - 0.15) / 0.85;
  return {
    position: [x, flight.altitude + Math.sin(angle * 2) * 1.7, -y] as Vec3,
    yaw: Math.atan2(vy, vx),
    bank: -0.17 + Math.sin(angle) * 0.04,
    wing: -0.08 + Math.sin(time * 5.7 + flight.phase) * 0.55 * glide,
  };
}
